import React, { Fragment, useEffect, useState } from "react";
import axios from "axios";
import BugReportSharpIcon from '@mui/icons-material/BugReportSharp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CleaningServicesRoundedIcon from '@mui/icons-material/CleaningServicesRounded';
import GrassSharpIcon from '@mui/icons-material/GrassSharp';
import { getAuth } from "firebase/auth";
import { useChartColors } from '../../hooks/useChartColors';
import { ChartCard } from './shared';
import BASE_URL from '../../config';



const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

function StatusChart({ selectedSiteCode }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const areaColors = useChartColors('--ruby-300', '--emerald-400', '--emerald-700', '--slate-gray', '--ruby-200', '--azure-500');

  const legendItems = [
    { label: 'Infested', color: areaColors['ruby-300'], icon: <BugReportSharpIcon /> },
    { label: 'Clean', color: areaColors['emerald-400'], icon: <CheckCircleIcon /> },
    { label: 'Germinated', color: areaColors['azure-500'], icon: <GrassSharpIcon /> },
    { label: 'Cleaned', color: areaColors['emerald-700'], icon: <CleaningServicesRoundedIcon /> },
  ];

  useEffect(() => {
    const fetchStatusData = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = getAuth().currentUser;
        const token = user && await user.getIdToken();

        let url = `${BASE_URL}/api/status`;
        if (selectedSiteCode) {
          url += `?site_id=${encodeURIComponent(selectedSiteCode)}`;
        }

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const formattedData = response.data.flatMap(row => {
          const rows = [];
          if (row.status) {
            rows.push({ month: row.date.substring(0, 3), year: row.date.substring(3), status: row.status });
          }
          if (row.status_2) {
            rows.push({ month: row.date.substring(0, 3), year: row.date.substring(3), status: row.status_2 });
          }
          return rows;
        });

        setData(formattedData);
      } catch (error) {
        console.error("Error fetching status data:", error);
        setError("Failed to load area statistics. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchStatusData();
  }, [selectedSiteCode]);

  return (
    <Fragment>
      <ChartCard
        title="Area Statistics"
        loading={loading}
        error={error}
        height="auto"
      >
        {data.length === 0 ? (
          <div className="no-data-message">
            <div style={{ fontWeight: 500 }}>No data found for the selected site</div>
          </div>
        ) : (
          <div className="chart-horizontal-container">
            <div className="chart-horizontal">
              {Array.from(new Set(data.map(d => d.year)))
                .sort((a, b) => parseInt(b) - parseInt(a))
                .slice(0, 3)
                .reverse()
                .map((year) => (
                  <div key={year} className="year-row">
                    <div className="year-label">{year}</div>
                    <div className="year-timeline">
                      {months.map((month, index) => {
                        const items = data.filter(d => d.year === year && d.month === month);
                        if (items.length > 0) {
                          const firstItem = items[0];
                          const middleItem = items[Math.floor(items.length / 2)];
                          const lastItem = items[items.length - 1];

                          const barColor = legendItems.find(l => l.label.toLowerCase() === lastItem.status.toLowerCase())?.color;
                          const startIcon = legendItems.find(l => l.label.toLowerCase() === firstItem.status.toLowerCase())?.icon;
                          const middleIcon = legendItems.find(l => l.label.toLowerCase() === middleItem.status.toLowerCase())?.icon;
                            const endIcon = legendItems.find(l => l.label.toLowerCase() === lastItem.status.toLowerCase())?.icon;

                            return (
                              <div key={`${year}-${month}`} className="chart-bar" style={{ backgroundColor: barColor }}>
                                {index === 0 && <div className="chart-icon">{startIcon}</div>}
                                {index === 5 && <div className="chart-icon">{middleIcon}</div>}
                                {index === 11 && <div className="chart-icon">{endIcon}</div>}
                              </div>
                            );
                          }
                          return <div key={`${year}-${month}`} className="chart-bar empty"></div>;
                        })}
                      </div>
                    </div>
                  ))}
            </div>
            <div className="x-axis">
              {months.map((month, index) => (
                <div key={index} className="x-label-lake">{month}</div>
              ))}
            </div>
          </div>
        )}

        {/* Legend - only show when data exists */}
        {!loading && !error && data.length > 0 && (
          <div className="legend">
            {legendItems.map(item => (
              <div className="legend-item" key={item.label}>
                <div className="legend-color-lake" style={{ backgroundColor: item.color }}></div>
                {item.icon}
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        )}
      </ChartCard>
    </Fragment>
  );
}

export default StatusChart;